// Date helpers for the To-do section. Calm framing: overdue items
// still appear under "Earlier" (not "OVERDUE!"), in amber not red.

export type Bucket = "Earlier" | "Today" | "Tomorrow" | "This week" | "Later" | "No date" | "Done";

export function bucketForDueAt(iso: string | null): Bucket {
  if (!iso) return "No date";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "No date";
  const startOfDayD = new Date(d).setHours(0, 0, 0, 0);
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  const diffDays = Math.floor((startOfDayD - startOfToday) / 86400000);
  if (diffDays < 0) return "Earlier";
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays <= 6) return "This week";
  return "Later";
}

export function formatDueAt(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    .replace(/:00\s/, " ")
    .toLowerCase();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return time;
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return `tmrw ${time}`;
  const startOfD = new Date(d).setHours(0, 0, 0, 0);
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  const diff = Math.floor((startOfD - startOfToday) / 86400000);
  if (diff > 0 && diff <= 6) {
    return `${d.toLocaleDateString([], { weekday: "short" })} ${time}`;
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function isOverdue(iso: string | null): boolean {
  if (!iso) return false;
  return new Date(iso) < new Date();
}

export function isSoon(iso: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso).getTime() - Date.now();
  return d > 0 && d < 6 * 3600 * 1000;
}

// Convert ISO → `YYYY-MM-DDTHH:MM` for <input type="datetime-local">.
export function toInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// And back to ISO (local timezone). Browsers' datetime-local omits TZ.
export function fromInputValue(s: string): string | null {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Split-picker helpers: <input type="date"> + <input type="time">.
// Each returns a string in the format the native input expects, and
// we recombine into a full ISO when either changes.

/** ISO → "YYYY-MM-DD" in local time. */
export function dateOnly(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** ISO → "HH:MM" in local time. */
export function timeOnly(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Compose "YYYY-MM-DD" + "HH:MM" → ISO. Returns null on empty/bad
 *  inputs. Used when either of the split pickers fires. */
export function combineDateTime(dateStr: string, timeStr: string): string | null {
  if (!dateStr) return null;
  // Default to 5pm when the user picks a date but not a time — most
  // "due today" items mean by end of day. They can edit in the time
  // picker after.
  const t = timeStr || "17:00";
  const d = new Date(`${dateStr}T${t}`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export interface PresetOffset {
  day?: number;  // 0=Sun .. 6=Sat (next occurrence)
  d?: number;    // days from now
  h?: number;    // set hour-of-day
  mins?: number; // minutes from now
}

export function buildPreset(offset: PresetOffset): string {
  const d = new Date();
  if (offset.day != null) {
    const cur = d.getDay();
    const add = ((offset.day - cur + 7) % 7) || 7;
    d.setDate(d.getDate() + add);
  }
  if (offset.d != null) d.setDate(d.getDate() + offset.d);
  if (offset.h != null) {
    d.setHours(offset.h); d.setMinutes(0); d.setSeconds(0, 0);
  }
  if (offset.mins != null) d.setMinutes(d.getMinutes() + offset.mins);
  return toInputValue(d.toISOString());
}

export const GROUP_ORDER: Bucket[] = ["Earlier", "Today", "Tomorrow", "This week", "Later", "No date", "Done"];

/** Tag for the "untagged" bucket in the by-tag grouping view. Empty
 *  string is the canonical "no tag" value across the todos surface. */
export const NO_TAG_BUCKET = "";
